import React, { useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { KeyRound, Eye, EyeOff, Shield, User, CheckCircle } from 'lucide-react';

export default function AdminSettings() {
  const { user } = useAuth();
  const [currentPass, setCurrentPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 5000); };

  const strength = (p) => {
    if (!p) return { score: 0, label: '', color: '#e2e8f0' };
    let score = 0;
    if (p.length >= 6) score++;
    if (p.length >= 10) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    const levels = [
      { label: '', color: '#e2e8f0' },
      { label: 'Sangat Lemah', color: '#ef4444' },
      { label: 'Lemah', color: '#f97316' },
      { label: 'Cukup', color: '#eab308' },
      { label: 'Kuat', color: '#22c55e' },
      { label: 'Sangat Kuat', color: '#16a34a' },
    ];
    return { score, ...levels[score] };
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPass !== confirmPass) { showToast('Password baru tidak cocok', 'error'); return; }
    if (newPass.length < 6) { showToast('Password minimal 6 karakter', 'error'); return; }
    setLoading(true);
    try {
      // Re-autentikasi dulu sebelum ganti password (wajib di Firebase)
      const credential = EmailAuthProvider.credential(user.email, currentPass);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPass);
      showToast('✅ Password berhasil diubah!');
      setCurrentPass(''); setNewPass(''); setConfirmPass('');
    } catch (e) {
      if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        showToast('❌ Password saat ini salah', 'error');
      } else if (e.code === 'auth/too-many-requests') {
        showToast('❌ Terlalu banyak percobaan. Coba lagi nanti.', 'error');
      } else {
        showToast('❌ Gagal: ' + e.message, 'error');
      }
    }
    setLoading(false);
  };

  const s = strength(newPass);

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="page-header">
          <h1>Pengaturan Akun</h1>
          <p>Kelola keamanan akun admin kamu.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>

          {/* Info akun */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--green-700)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                <User size={24} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: 'var(--green-900)', fontSize: 16 }}>Admin</div>
                <div style={{ fontSize: 13, color: '#0369a1' }}>{user?.email}</div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '1rem' }}>
              {[
                { label: 'Email', value: user?.email },
                { label: 'Role', value: 'Administrator' },
                { label: 'UID Firebase', value: user?.uid?.substring(0, 16) + '...' },
                { label: 'Login Terakhir', value: user?.metadata?.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleString('id-ID') : '-' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < 3 ? '1px solid var(--border)' : 'none', fontSize: 13 }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{item.label}</span>
                  <span style={{ color: 'var(--text)', fontFamily: item.label === 'UID Firebase' ? 'monospace' : 'inherit' }}>{item.value}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '1.25rem', padding: '12px 14px', background: 'var(--green-50)', borderRadius: 10, fontSize: 13, color: 'var(--green-800)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, marginBottom: 6 }}>
                <Shield size={14} /> Tips Keamanan
              </div>
              <ul style={{ listStyle: 'none', paddingLeft: 0, lineHeight: 1.8 }}>
                <li>✅ Gunakan password yang unik dan kuat</li>
                <li>✅ Jangan bagikan password ke siapapun</li>
                <li>✅ Ganti password secara berkala</li>
                <li>✅ Logout setelah selesai menggunakan</li>
              </ul>
            </div>
          </div>

          {/* Form ganti password */}
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.5rem' }}>
              <KeyRound size={20} color="var(--green-700)" />
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--green-900)' }}>Ganti Password Admin</h3>
            </div>

            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label className="form-label">Password Saat Ini</label>
                <div style={{ position: 'relative' }}>
                  <input type={showCurrent ? 'text' : 'password'} className="form-input"
                    placeholder="Masukkan password lama" value={currentPass}
                    onChange={e => setCurrentPass(e.target.value)} required style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowCurrent(!showCurrent)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                    {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Password Baru</label>
                <div style={{ position: 'relative' }}>
                  <input type={showNew ? 'text' : 'password'} className="form-input"
                    placeholder="Min. 6 karakter" value={newPass}
                    onChange={e => setNewPass(e.target.value)} required style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowNew(!showNew)}
                    style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                    {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {newPass && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                      {[1,2,3,4,5].map(i => (
                        <div key={i} style={{ flex: 1, height: 4, borderRadius: 999, background: i <= s.score ? s.color : '#e2e8f0', transition: 'background 0.3s' }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.label}</div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Konfirmasi Password Baru</label>
                <div style={{ position: 'relative' }}>
                  <input type="password" className="form-input"
                    placeholder="Ulangi password baru" value={confirmPass}
                    onChange={e => setConfirmPass(e.target.value)} required style={{ paddingRight: 36 }} />
                  {confirmPass && (
                    <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                      {confirmPass === newPass
                        ? <CheckCircle size={15} color="#16a34a" />
                        : <span style={{ color: '#dc2626', fontSize: 16 }}>✗</span>}
                    </div>
                  )}
                </div>
                {confirmPass && confirmPass !== newPass && (
                  <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>Password tidak cocok</div>
                )}
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '12px' }}
                disabled={loading || !currentPass || !newPass || !confirmPass || newPass !== confirmPass}>
                {loading ? '⏳ Menyimpan...' : '🔐 Simpan Password Baru'}
              </button>
            </form>

            <div style={{ marginTop: '1rem', padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 12, color: '#92400e', lineHeight: 1.7 }}>
              <strong>Catatan:</strong> Perubahan password berlaku langsung. Kamu tidak akan ter-logout, tapi kurir yang sedang login dengan akun yang sama akan perlu login ulang jika passwordnya diganti.
            </div>
          </div>

        </div>

        {toast && (
          <div className="toast-container">
            <div className={`toast toast-${toast.type}`}>{toast.msg}</div>
          </div>
        )}
      </div>
    </div>
  );
}
